import { createSelector } from '@reduxjs/toolkit'
import { format, parse } from 'date-fns'

import { DELAY_NOTIFICATION_DELAY_THRESHOLD_MINUTES } from '../constants'
import {
  getQualifyingEquipmentFaultDelayRows,
  getTaskDelayNotificationKey,
  TimeTableType,
} from '../lib/taskDelayUtils'
import { isPassageTravelTask, selectOperatingDateForTask } from '../Selectors'
import { Task } from '../types'
import { TrainPunctuality } from '../types/Input'
import type { PendingDelayNotification } from './delayNotificationSlice'

type StateSlice = {
  user: { driver: boolean; number: string }
  delayNotifications: {
    pendingPrevious: PendingDelayNotification[]
    handledNotifications: Record<string, boolean>
  }
}

const amendmentPathFor = (
  trainNumber: string,
  operatingDate: string,
  station: string,
  type: TimeTableType = 'ARRIVAL'
): string => `/amendDeviationReason/${trainNumber}/${operatingDate}/${station}/${type}`

// ---------------------------------------------------------------------------
// Previous-shift notifications (bar 1 position on the shift page)
// ---------------------------------------------------------------------------

export interface PreviousDelayVM {
  notificationKey: string
  kind: PendingDelayNotification['kind']
  detail: { date: string; trainNumber: string; fromStation: string; toStation: string }
  amendmentPath: string
}

export const selectPreviousShiftDelayVMs = createSelector(
  (state: StateSlice) => state.user.driver,
  (state: StateSlice) => state.delayNotifications.pendingPrevious,
  (isDriver, pendingPrevious): PreviousDelayVM[] => {
    if (!isDriver) return []

    return pendingPrevious.map((pending) => {
      return {
        notificationKey: pending.notificationKey,
        kind: pending.kind,
        detail: {
          // departureDate is a plain YYYY-MM-DD; parse as a local date so
          // the day never shifts across timezones.
          date: format(parse(pending.departureDate, 'yyyy-MM-dd', new Date()), 'd.M.yyyy'),
          trainNumber: pending.trainNumber,
          fromStation: pending.fromStation,
          toStation: pending.toStation,
        },
        amendmentPath: amendmentPathFor(
          pending.trainNumber,
          pending.departureDate,
          pending.delayedStation,
          pending.delayedRowType === 'DEPARTURE' ? 'DEPARTURE' : 'ARRIVAL'
        ),
      }
    })
  }
)

// ---------------------------------------------------------------------------
// Per-task notification (current shift)
// ---------------------------------------------------------------------------

export interface TaskDelayInputs {
  isDriver: boolean
  punctualityByTrain: Record<string, TrainPunctuality>
}

export interface TaskDelayVM {
  notificationKey: string
  amendmentPath: string
}

/**
 * Decides whether a task's delay notification should show and with what
 * content. Returns null when the bar must not render: not a driver, driver not
 * on the delayed leg, delay under threshold, or no K/V cause.
 */
export const buildTaskDelayVM = (
  { isDriver, punctualityByTrain }: TaskDelayInputs,
  task: Task
): TaskDelayVM | null => {
  if (!isDriver || isPassageTravelTask(task)) return null

  const operatingDate = selectOperatingDateForTask(task)
  const punctuality = task.trainNumberNumeric
    ? punctualityByTrain[`${task.trainNumberNumeric}+${operatingDate}`]
    : undefined
  if (!punctuality) return null

  const qualifyingDelayRows = getQualifyingEquipmentFaultDelayRows(
    task.fromStation,
    task.toStation,
    punctuality,
    DELAY_NOTIFICATION_DELAY_THRESHOLD_MINUTES
  )
  if (qualifyingDelayRows.length === 0) return null

  // Link the amendment to the first qualifying row (where the fault first
  // manifested) — it is not necessarily the destination arrival. Matches the
  // row the backend uses for delayOccurredAt.
  const delayedRow = qualifyingDelayRows[0]

  return {
    notificationKey: getTaskDelayNotificationKey(task),
    amendmentPath: amendmentPathFor(
      task.trainNumberNumeric,
      operatingDate,
      delayedRow.stationShortCode,
      delayedRow.type
    ),
  }
}
